import { Link, useOutletContext } from '@remix-run/react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { cn } from '#app/utils/misc.tsx'
import { CompoundCurve } from './compound-curve.tsx'
import { GridBackdrop } from './grid-backdrop.tsx'

type Decoration = 'curve' | 'grid'

export function MarketingHero({
	eyebrow,
	heading,
	subhead,
	ctaLabel = 'Sign up',
	ctaHref = '/signup',
	decorations = [],
	variant = 'default',
	children,
}: {
	eyebrow: string
	heading: string
	subhead?: string
	ctaLabel?: string
	ctaHref?: string
	decorations?: Decoration[]
	variant?: 'default' | 'compact'
	children?: React.ReactNode
}) {
	const padding =
		variant === 'compact'
			? 'px-6 py-20 md:px-10 md:py-24'
			: 'px-6 py-28 md:px-10 md:py-40'
	const ctx = useOutletContext<{ signupEnabled?: boolean } | null>()
	const signupDisabled = ctaHref === '/signup' && !ctx?.signupEnabled
	const resolvedCtaHref = signupDisabled ? '/login' : ctaHref
	const resolvedCtaLabel =
		signupDisabled && ctaLabel === 'Sign up' ? 'Log in' : ctaLabel
	return (
		<section className="relative overflow-hidden bg-ink text-ink-fg">
			{decorations.includes('grid') ? <GridBackdrop /> : null}
			{decorations.includes('curve') ? (
				<CompoundCurve
					className="absolute bottom-8 right-8 hidden md:block"
					width={200}
					height={90}
				/>
			) : null}
			<div className={cn('relative mx-auto w-full max-w-5xl', padding)}>
				<EyebrowLabel>{eyebrow}</EyebrowLabel>
				<h1
					className={cn(
						'mt-5 font-display',
						variant === 'compact' ? 'text-display-2' : 'text-display-1',
					)}
				>
					{heading}
				</h1>
				{subhead ? (
					<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft md:text-lg">
						{subhead}
					</p>
				) : null}
				{resolvedCtaLabel ? (
					<div className="mt-10">
						<Link
							to={resolvedCtaHref}
							className="inline-block rounded-md bg-ember px-5 py-3 text-sm font-medium text-ink hover:opacity-90"
						>
							{resolvedCtaLabel}
						</Link>
					</div>
				) : null}
				{children ? <div className="mt-12">{children}</div> : null}
			</div>
		</section>
	)
}
